import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { MessageCircle, ArrowLeft, Flame, Soup, Salad, Sparkles, Cookie, UtensilsCrossed, Drumstick, Leaf, FileDown, ChevronDown, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { API, waLink, PRODUCT_LINKS } from "../config";
import { meta, productParams } from "../lib/metaPixel";
import { useCart } from "../cart/CartContext";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhereToBuy } from "../components/WhereToBuy";

const ICONS = { Flame, Soup, Salad, Sparkles, Cookie, UtensilsCrossed, Drumstick };

const PRODUCT_SEO = {
  "black-mustard-oil": { title: "Cold-Pressed Mustard Oil | Kachi Ghani Mustard Oil | PhudG", description: "Discover PhudG Cold-Pressed Mustard Oil with its bold aroma and authentic flavour, ideal for Indian cooking, tadka, pickles and marinades." },
  "yellow-mustard-oil": { title: "Cold-Pressed Yellow Mustard Oil | Kachi Ghani | PhudG", description: "Discover PhudG Cold-Pressed Yellow Mustard Oil — mild, aromatic and wood-pressed, ideal for everyday Indian cooking, pickles and marinades." },
  "groundnut-oil": { title: "Cold-Pressed Groundnut Oil | Peanut Oil for Cooking | PhudG", description: "Discover PhudG Cold-Pressed Groundnut Oil with its naturally nutty flavour, suitable for everyday Indian cooking, tadka and frying." },
  "sunflower-oil": { title: "Cold-Pressed Sunflower Oil | Pure Sunflower Cooking Oil | PhudG", description: "Explore PhudG Cold-Pressed Sunflower Oil with a mild flavour, made for everyday Indian cooking, sautéing and a variety of dishes." },
};

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const MiniFaq = ({ faqs, slug }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="divide-y divide-ink/10 border-y border-ink/10">
      {faqs.map((f, i) => (
        <div key={i}>
          <button
            data-testid={`product-faq-${slug}-${i}`}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="flex w-full items-center justify-between gap-6 py-5 text-left"
          >
            <span className="font-serif text-xl text-ink">{f.q}</span>
            <ChevronDown size={18} className={`shrink-0 text-amberbrand transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="overflow-hidden"
              >
                <p className="pb-5 max-w-2xl text-sm font-light text-ink/60 leading-relaxed">{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [others, setOthers] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [sizeIdx, setSizeIdx] = useState(0);
  const { add } = useCart();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    axios
      .get(`${API}/products/${slug}`)
      .then((res) => {
        setProduct(res.data);
        const priced = res.data.sizes?.findIndex((s) => s.price === res.data.price);
        setSizeIdx(priced >= 0 ? priced : 0);
      })
      .catch(() => setNotFound(true));
    axios
      .get(`${API}/products`, { params: { status: "available" } })
      .then((res) => setOthers(res.data.filter((p) => p.slug !== slug)))
      .catch(() => {});
  }, [slug]);

  const selectedSize = product?.sizes?.[sizeIdx];

  useEffect(() => {
    if (product && selectedSize) {
      meta.viewContent(productParams({
        slug: product.slug,
        name: product.name,
        size: selectedSize.label,
        price: selectedSize.price ?? product.price ?? 0,
      }));
    }
  }, [product, selectedSize]);

  const cartItem = () => ({
    slug: product.slug,
    name: product.name,
    image: product.cart_image || product.image,
    size: selectedSize.label,
    price: selectedSize.price,
  });

  const buyNow = () => {
    if (!selectedSize?.price) {
      toast.error("This size isn't available for online payment — order on WhatsApp instead.");
      return;
    }
    add(cartItem());
    navigate("/cart");
  };

  const addToCart = () => {
    if (!selectedSize?.price) {
      toast.error("This size isn't available online yet — order on WhatsApp instead.");
      return;
    }
    add(cartItem());
    toast.success(`${product.name} (${selectedSize.label}) added to cart`);
  };

  const orderViaWhatsApp = () => {
    if (!selectedSize?.price) {
      meta.lead({ content_name: product.name, currency: "INR" });
      window.open(
        waLink(`Hi PHUDG! I'd like to order the ${product.name} (${selectedSize?.label || product.unit}).`),
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    add(cartItem());
    navigate("/cart");
  };

  return (
    <main data-testid="product-detail-page">
      <Header />

      {notFound && (
        <section className="min-h-screen bg-ink flex flex-col items-center justify-center text-center px-6">
          <p data-testid="product-not-found" className="font-serif text-4xl text-cream mb-6">
            This oil hasn't been bottled yet.
          </p>
          <Link
            to="/"
            data-testid="not-found-home-link"
            className="rounded-full border border-gold/40 px-6 py-3 text-sm text-gold hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            Back to the pantry
          </Link>
        </section>
      )}

      {product && (
        <>
          <Seo title={PRODUCT_SEO[product.slug]?.title || `${product.name} | PhudG`} description={PRODUCT_SEO[product.slug]?.description || product.description} image={product.hero_image || product.image} />
          <section ref={heroRef} className="relative bg-ink grain overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
            <div className="absolute -left-40 top-1/3 h-[30rem] w-[30rem] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 items-center">
              <div className="md:col-span-6">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                  <Link
                    to="/"
                    data-testid="back-to-home-link"
                    className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-cream/50 hover:text-gold transition-colors duration-300 mb-8"
                  >
                    <ArrowLeft size={14} /> All Oils
                  </Link>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                  className="text-xs tracking-[0.4em] uppercase text-gold mb-4"
                >
                  {product.tagline}
                </motion.p>
                <div className="overflow-hidden mb-6">
                  <motion.h1
                    data-testid="detail-name"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="font-serif font-semibold text-5xl md:text-7xl text-cream tracking-tighter leading-[0.95]"
                  >
                    {product.name}
                  </motion.h1>
                </div>
                <motion.p
                  data-testid="detail-description"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="max-w-lg text-base font-light text-cream/70 leading-relaxed mb-8"
                >
                  {product.description}
                </motion.p>

                {product.sizes?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55 }}
                    className="flex gap-3 mb-6"
                  >
                    {product.sizes.map((s, i) => (
                      <button
                        key={s.label}
                        data-testid={`size-option-${i}`}
                        onClick={() => setSizeIdx(i)}
                        className={`rounded-full px-5 py-2.5 text-sm transition-colors duration-300 ${
                          sizeIdx === i
                            ? "bg-gold text-ink font-medium"
                            : "border border-cream/25 text-cream/70 hover:border-gold hover:text-gold"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.65 }}
                  className="flex flex-wrap items-center gap-5"
                >
                  <p data-testid="detail-price" className="font-serif text-4xl text-gold">
                    {selectedSize?.price ? (
                      <>
                        ₹{selectedSize.price}
                        <span className="text-base text-cream/50 font-sans font-light"> / {selectedSize.label}</span>
                      </>
                    ) : (
                      <span className="text-2xl italic text-cream/60">Price on enquiry</span>
                    )}
                  </p>
                  <motion.button
                    data-testid="detail-buy-now-btn"
                    onClick={buyNow}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="rounded-full bg-gold px-8 py-4 text-sm font-medium text-ink hover:bg-amberbrand hover:text-cream transition-colors duration-300"
                  >
                    {selectedSize?.price ? `Pay ₹${selectedSize.price} Online` : "Pay Online"}
                  </motion.button>
                  <motion.button
                    data-testid="detail-add-to-cart-btn"
                    onClick={addToCart}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="rounded-full border border-cream/25 px-6 py-4 text-sm font-light text-cream/80 hover:border-gold hover:text-gold transition-colors duration-300"
                  >
                    Add to Cart
                  </motion.button>
                  <motion.button
                    data-testid="detail-buy-btn"
                    onClick={orderViaWhatsApp}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="flex items-center gap-2 rounded-full border border-cream/25 px-6 py-4 text-sm font-light text-cream/80 hover:border-gold hover:text-gold transition-colors duration-300"
                  >
                    <MessageCircle size={18} />
                    WhatsApp
                  </motion.button>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.85 }}
                  className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
                >
                  {["Wooden Ghani Pressed", "Below 45°C", "Small Batches", "Free Delivery"].map((badge) => (
                    <span key={badge} className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-cream/50">
                      <Leaf size={13} className="text-gold/70" /> {badge}
                    </span>
                  ))}
                </motion.div>
                {PRODUCT_LINKS[product.slug] && (
                  <motion.div
                    data-testid="detail-marketplace-links"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2"
                  >
                    <span className="text-[10px] tracking-[0.3em] uppercase text-cream/40">Also on</span>
                    {[
                      { key: "amazon", label: "Amazon" },
                      { key: "instamart", label: "Instamart" },
                      { key: "blinkit", label: "Blinkit" },
                    ]
                      .filter((m) => PRODUCT_LINKS[product.slug][m.key])
                      .map((m) => (
                        <a
                          key={m.key}
                          data-testid={`detail-buy-${m.key}`}
                          href={PRODUCT_LINKS[product.slug][m.key]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-gold/80 underline underline-offset-4 hover:text-gold transition-colors duration-300"
                        >
                          {m.label} <ArrowUpRight size={13} />
                        </a>
                      ))}
                  </motion.div>
                )}
              </div>

              <div className="md:col-span-6">
                <motion.div
                  style={{ y: imgY }}
                  initial={{ opacity: 0, y: 60 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="relative mx-auto max-w-md"
                >
                  <div className="animate-float-slow">
                    <div
                      data-testid="detail-hero-image"
                      className="overflow-hidden rounded-t-[999px] rounded-b-3xl border border-gold/30 shadow-[0_40px_80px_-20px_rgba(212,175,55,0.25)]"
                    >
                      <img
                        src={product.hero_image || product.image}
                        alt={`PHUDG ${product.name} bottle`}
                        className="w-full aspect-[3/4] object-cover"
                      />
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          <section data-testid="detail-story-section" className="bg-cream py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 items-center">
              <motion.div {...fadeUp} className="md:col-span-5">
                <div className="overflow-hidden rounded-3xl border border-ink/10">
                  <img
                    src={product.story_image || product.image}
                    alt={`${product.name} in the kitchen`}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>
              </motion.div>
              <div className="md:col-span-7">
                <motion.p {...fadeUp} className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-4">
                  What it is
                </motion.p>
                <motion.h2
                  data-testid="detail-story-title"
                  {...fadeUp}
                  className="font-serif text-4xl md:text-6xl text-ink tracking-tighter mb-8"
                >
                  {product.story_title}
                </motion.h2>
                {product.story.map((para, i) => (
                  <motion.p
                    key={i}
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: 0.1 + i * 0.1 }}
                    className="text-base font-light text-ink/65 leading-relaxed mb-5 max-w-xl"
                  >
                    {para}
                  </motion.p>
                ))}
                {product.ingredients && (
                  <motion.div
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: 0.3 }}
                    className="mt-8 rounded-2xl border border-gold/30 bg-gold/5 p-6 max-w-xl"
                  >
                    <p className="text-[10px] tracking-[0.3em] uppercase text-amberbrand mb-2">Ingredients</p>
                    <p data-testid="detail-ingredients" className="font-serif text-lg italic text-ink/75">
                      {product.ingredients}
                    </p>
                  </motion.div>
                )}
              </div>
            </div>
          </section>

          <section data-testid="detail-nutrition-section" className="bg-ink grain py-24 md:py-32 relative overflow-hidden">
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
              <motion.p {...fadeUp} className="text-xs tracking-[0.4em] uppercase text-gold mb-4">
                What's Inside
              </motion.p>
              <motion.h2 {...fadeUp} className="font-serif text-4xl md:text-6xl text-cream tracking-tighter mb-14">
                Nutrition, <span className="italic text-gold">nothing hidden.</span>
              </motion.h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-gold/15 border border-gold/15">
                {product.nutrition.map((n, i) => (
                  <motion.div
                    key={n.label}
                    data-testid={`nutrition-cell-${i}`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                    className="bg-ink p-8 md:p-10"
                  >
                    <p className="font-serif text-3xl md:text-4xl text-gold mb-2">{n.value}</p>
                    <p className="text-[11px] tracking-[0.25em] uppercase text-cream/50">{n.label}</p>
                  </motion.div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                <p className="text-xs font-light text-cream/35">
                  Lab-tested values per 100 g — the full report is yours to download.
                </p>
                {product.lab_report && (
                  <a
                    data-testid="detail-lab-report-link"
                    href={product.lab_report}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-gold/70 underline underline-offset-4 hover:text-gold transition-colors duration-300"
                  >
                    <FileDown size={13} /> Download the full lab report (PDF)
                  </a>
                )}
              </div>
            </div>
          </section>

          <section data-testid="detail-usage-section" className="bg-cream py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
              <motion.p {...fadeUp} className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-4">
                In Your Kitchen
              </motion.p>
              <motion.h2 {...fadeUp} className="font-serif text-4xl md:text-6xl text-ink tracking-tighter mb-14">
                Made for <span className="italic text-olive">real cooking.</span>
              </motion.h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {product.usages.map((u, i) => {
                  const Icon = ICONS[u.icon] || Flame;
                  return (
                    <motion.div
                      key={u.title}
                      data-testid={`usage-card-${i}`}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                      whileHover={{ y: -8 }}
                      className="rounded-3xl border border-ink/10 bg-white/70 p-8 transition-colors duration-300 hover:border-gold/50"
                    >
                      <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-olive/10 text-olive">
                        <Icon size={20} />
                      </span>
                      <h3 className="font-serif text-2xl text-ink mb-3">{u.title}</h3>
                      <p className="text-sm font-light text-ink/55 leading-relaxed">{u.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {product.faqs?.length > 0 && (
            <section data-testid="detail-faq-section" className="bg-cream pb-24 md:pb-32">
              <div className="max-w-4xl mx-auto px-6">
                <motion.p {...fadeUp} className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-4">
                  Good to Know
                </motion.p>
                <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl text-ink tracking-tighter mb-10">
                  Questions about <span className="italic text-olive">this oil.</span>
                </motion.h2>
                <MiniFaq faqs={product.faqs} slug={product.slug} />
              </div>
            </section>
          )}

          <WhereToBuy />

          {others.length > 0 && (
            <section data-testid="detail-related-section" className="bg-cream pb-24 md:pb-32 border-t border-ink/10 pt-24">
              <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl text-ink tracking-tighter mb-12">
                  Also from <span className="italic text-olive">the ghani.</span>
                </motion.h2>
                <div className="grid sm:grid-cols-3 gap-8">
                  {others.map((p, i) => (
                    <motion.div
                      key={p.slug}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.7, delay: i * 0.1 }}
                    >
                      <Link
                        to={`/oils/${p.slug}`}
                        data-testid={`related-card-${p.slug}`}
                        className="group block"
                      >
                        <div className="overflow-hidden rounded-3xl border border-ink/10 mb-5">
                          <img
                            src={p.image}
                            alt={`PHUDG ${p.name}`}
                            loading="lazy"
                            className="w-full aspect-[4/5] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                          />
                        </div>
                        <h3 className="font-serif text-2xl text-ink group-hover:text-amberbrand transition-colors duration-300">
                          {p.name}
                        </h3>
                        <p className="text-sm font-light text-ink/50 mt-1">
                          {p.price ? `₹${p.price} / ${p.unit}` : "Price on enquiry"}
                        </p>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <Footer />
    </main>
  );
}
